import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import Page from '../../components/Page';
import Label from '../../components/Label';
import { useAppDispatch, useAppSelector } from '../../store';
import { findAdvertise } from '../../store/advertise/api';
import {
  setCurrentPage,
  setLimit,
  setSkip,
} from '../../store/advertise/advertiseSlice';
import Loader from '../../components/Loader';
import { DateFormat } from '../../utils/formatTime';
import { selectAdvertiseStatusColor } from '../../utils/selectAdvertiseStatusColor';
import { encryptString } from '../../utils/url';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Iconify from '../../components/Iconify';
import AddIcon from '@mui/icons-material/Add';
import { AdvertiseStatus, SortBy } from '../../enum';
import { publishAdsWithCredits } from '../../services/company-credits';
import { createChangeAdvertiseStatus } from '../../store/changeAdvertiseStatus/api';
import { companyAdvertiseBulkStatus } from '../../store/companyAdvertiseBulkStatus/api';
import { toast } from 'react-toastify';

// Custom hook for navigating to detail while preserving state
const useDetailNavigate = () => {
  const navigate = useNavigate();
  
  const goToDetail = (id: string | undefined) => {
    navigate(`/company-advertise/view/${encryptString(id ?? '')}`, {
      state: { fromListing: true }
    });
  };
  
  return goToDetail;
};

const DEFAULT_AD_SORT = {
  sortByName: SortBy.updatedAt,
  sortByOrder: 'DESC' as const,
};

const CompanyAdvertise = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const goToDetail = useDetailNavigate();
  const { data, totalRecords, limit, currentPage, skip, isLoading } =
    useAppSelector((state) => state.advertise);
  const userRole = useAppSelector((state) => state.auth.user?.userRole);
  const [search, setSearch] = useState('');
  const [adType, setAdType] = useState<string>('all'); // 'all', 'company', 'agent'
  const [statusFilter, setStatusFilter] = useState<string>(''); // Status filter
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());
  const [publishingIds, setPublishingIds] = useState<Set<string>>(new Set());
  const [unpublishingIds, setUnpublishingIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const initialRender = useRef(true);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Handle API calls with adType filter
  useEffect(() => {
    if (!initialRender.current) {
      dispatch(setSkip({ skip: 0 }));
      dispatch(setCurrentPage({ currentPage: 0 }));
    }
    
    if (!initialRender.current || adType) {
      dispatch(
        findAdvertise({
          limit,
          skip,
          ...(search && { search: search }),
          adType: adType, // Pass adType to filter company/agent ads
          ...(statusFilter && { status: statusFilter }),
          ...DEFAULT_AD_SORT,
        })
      );
    }
    
    if (initialRender.current) {
      initialRender.current = false;
    }
  }, [
    dispatch,
    limit,
    skip,
    search,
    adType,
    statusFilter,
  ]);

  // Reset to first page when adType changes
  useEffect(() => {
    if (!initialRender.current) {
      dispatch(setSkip({ skip: 0 }));
      dispatch(setCurrentPage({ currentPage: 0 }));
    }
  }, [adType, dispatch]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    dispatch(setLimit({ limit: parseInt(event.target.value, 10) }));
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setAdType(newValue);
    // Reset to first page when tab changes
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  // Debounced search handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      dispatch(setSkip({ skip: 0 }));
      dispatch(setCurrentPage({ currentPage: 0 }));
    }, 500);
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle publish action for company admins
  const handlePublish = async (advertiseId: string) => {
    if (publishingIds.has(advertiseId)) return; // Prevent duplicate clicks
    
    setPublishingIds((prev) => new Set(prev).add(advertiseId));
    
    try {
      const result = await publishAdsWithCredits({
        advertiseIds: [advertiseId],
      });
      
      toast.success(result.message || 'Ad published successfully using credits.');
      
      // Refresh the list
      dispatch(
        findAdvertise({
          limit,
          skip,
          ...(search && { search: search }),
          adType: adType,
          ...DEFAULT_AD_SORT,
        })
      );
    } catch (error: any) {
      console.error('Error publishing ad with credits:', error);
      toast.error(error?.message || 'Failed to publish ad. Please check your credit balance.');
    } finally {
      setPublishingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(advertiseId);
        return newSet;
      });
    }
  };

  // Handle unpublish action for company admins
  const handleUnpublish = async (advertiseId: string) => {
    if (unpublishingIds.has(advertiseId)) return; // Prevent duplicate clicks
    
    setUnpublishingIds((prev) => new Set(prev).add(advertiseId));
    
    try {
      await dispatch(
        createChangeAdvertiseStatus({
          advertiseId: advertiseId,
          status: AdvertiseStatus.unPublished,
        })
      ).unwrap();
      
      toast.success('Ad unpublished successfully.');
      
      // Refresh the list
      dispatch(
        findAdvertise({
          limit,
          skip,
          ...(search && { search: search }),
          adType: adType,
          ...DEFAULT_AD_SORT,
        })
      );
    } catch (error: any) {
      console.error('Error unpublishing ad:', error);
      toast.error(error?.message || 'Failed to unpublish ad.');
    } finally {
      setUnpublishingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(advertiseId);
        return newSet;
      });
    }
  };

  // Check if status can be published (for company admins)
  const canPublish = (status: string) => {
    return userRole === 'company' && (
      status === AdvertiseStatus.approved ||
      status === AdvertiseStatus.expired ||
      status === AdvertiseStatus.unPublished
    );
  };

  // Check if status can be unpublished (for company admins)
  const canUnpublish = (status: string) => {
    return userRole === 'company' && status === AdvertiseStatus.published;
  };

  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = new Set(data.map((ad: any) => ad.id));
      setSelectedAds(allIds);
    } else {
      setSelectedAds(new Set());
    }
  };

  // Handle individual checkbox
  const handleSelectAd = (adId: string) => {
    setSelectedAds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(adId)) {
        newSet.delete(adId);
      } else {
        newSet.add(adId);
      }
      return newSet;
    });
  };

  // Get common status of selected ads
  const getSelectedAdsCommonStatus = () => {
    if (selectedAds.size === 0) return null;
    const selectedAdData = data.filter((ad: any) => selectedAds.has(ad.id));
    if (selectedAdData.length === 0) return null;
    const firstStatus = selectedAdData[0].status;
    const allSameStatus = selectedAdData.every((ad: any) => ad.status === firstStatus);
    return allSameStatus ? firstStatus : null;
  };

  // Handle bulk publish
  const handleBulkPublish = async () => {
    if (selectedAds.size === 0) return;
    
    const selectedAdIds = Array.from(selectedAds);
    setBulkProcessing(true);
    setPublishingIds(new Set(selectedAdIds));
    
    try {
      const result = await publishAdsWithCredits({
        advertiseIds: selectedAdIds,
      });
      
      toast.success(result.message || `${selectedAdIds.length} ads published successfully using credits.`);
      setSelectedAds(new Set());
      
      // Refresh the list
      dispatch(
        findAdvertise({
          limit,
          skip,
          ...(search && { search: search }),
          adType: adType,
          ...(statusFilter && { status: statusFilter }),
          ...DEFAULT_AD_SORT,
        })
      );
    } catch (error: any) {
      console.error('Error bulk publishing ads:', error);
      toast.error(error?.message || 'Failed to publish ads. Please check your credit balance.');
    } finally {
      setBulkProcessing(false);
      setPublishingIds(new Set());
    }
  };

  // Handle bulk unpublish
  const handleBulkUnpublish = async () => {
    if (selectedAds.size === 0) return;
    
    const selectedAdIds = Array.from(selectedAds);
    setBulkProcessing(true);
    setUnpublishingIds(new Set(selectedAdIds));
    
    try {
      const result = await dispatch(
        companyAdvertiseBulkStatus({
          advertiseIds: selectedAdIds,
          status: AdvertiseStatus.unPublished,
        })
      ).unwrap();
      
      toast.success(result.message || `${selectedAdIds.length} ads unpublished successfully.`);
      setSelectedAds(new Set());
      
      // Refresh the list
      dispatch(
        findAdvertise({
          limit,
          skip,
          ...(search && { search: search }),
          adType: adType,
          ...(statusFilter && { status: statusFilter }),
          ...DEFAULT_AD_SORT,
        })
      );
    } catch (error: any) {
      console.error('Error bulk unpublishing ads:', error);
      toast.error(error?.message || 'Failed to unpublish ads.');
    } finally {
      setBulkProcessing(false);
      setUnpublishingIds(new Set());
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setSelectedAds(new Set()); // Clear selection when filter changes
    dispatch(setSkip({ skip: 0 }));
    dispatch(setCurrentPage({ currentPage: 0 }));
  };

  return (
    <Page title={'Company Advertise: List'}>
      <Container maxWidth={'xl'}>
        <Card>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            mb={3}
            sx={{ p: 2 }}
            spacing={2}
          >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
              <TextField
                fullWidth
                value={search}
                onChange={handleSearchChange}
                placeholder="Search advertise..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify
                        icon={'eva:search-fill'}
                        sx={{ color: 'text.disabled', width: 20, height: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel id="status-filter-label" sx={{ textAlign: 'left' }}>Status Filter</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status Filter"
                size="small"
                sx={{ textAlign: 'left' }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value={AdvertiseStatus.draft}>Draft</MenuItem>
                <MenuItem value={AdvertiseStatus.submit}>Submit</MenuItem>
                <MenuItem value={AdvertiseStatus.approved}>Approved</MenuItem>
                <MenuItem value={AdvertiseStatus.rejected}>Rejected</MenuItem>
                <MenuItem value={AdvertiseStatus.published}>Published</MenuItem>
                <MenuItem value={AdvertiseStatus.expired}>Expired</MenuItem>
                <MenuItem value={AdvertiseStatus.suspended}>Suspended</MenuItem>
                <MenuItem value={AdvertiseStatus.unPublished}>Unpublished</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/company-advertise/create')}
            >
              Create Ad
            </Button>
          </Stack>

          {/* Bulk Actions Bar */}
          {selectedAds.size > 0 && (
            <Box sx={{ p: 2, bgcolor: 'action.selected', borderBottom: 1, borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip label={`${selectedAds.size} selected`} color="primary" />
                {bulkProcessing && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )}
                {(() => {
                  const commonStatus = getSelectedAdsCommonStatus();
                  if (!commonStatus) return null;
                  
                  return (
                  <>
                      {commonStatus === AdvertiseStatus.published && (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={handleBulkUnpublish}
                        disabled={bulkProcessing}
                        startIcon={
                          bulkProcessing ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Iconify icon={'eva:close-circle-2-fill'} width={16} />
                          )
                        }
                      >
                        {bulkProcessing ? 'Unpublishing...' : 'Bulk Unpublish'}
                      </Button>
                    )}
                      {canPublish(commonStatus) && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleBulkPublish}
                        disabled={bulkProcessing}
                        startIcon={
                          bulkProcessing ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Iconify icon={'eva:checkmark-circle-2-fill'} width={16} />
                          )
                        }
                      >
                        {bulkProcessing 
                          ? 'Publishing...' 
                            : `Bulk ${commonStatus === AdvertiseStatus.expired || commonStatus === AdvertiseStatus.unPublished ? 'Republish' : 'Publish'}`}
                      </Button>
                    )}
                  </>
                  );
                })()}
                <Button
                  size="small"
                  onClick={() => setSelectedAds(new Set())}
                  disabled={bulkProcessing}
                >
                  Clear Selection
                </Button>
              </Stack>
            </Box>
          )}

          {/* Tabs for filtering */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={adType}
              onChange={handleTabChange}
              aria-label="ad type tabs"
            >
              <Tab label="All Ads" value="all" />
              <Tab label="Company Ads" value="company" />
              <Tab label="Agent Ads" value="agent" />
            </Tabs>
          </Box>

          {isLoading ? (
            <Box sx={{ py: 5 }}>
              <Loader />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
                <Table sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedAds.size > 0 && selectedAds.size < data.length}
                            checked={data.length > 0 && selectedAds.size === data.length}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Ad Number</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Created By</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell align="right" sx={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data && data.length > 0 ? (
                        data.map((row: any) => (
                          <TableRow hover key={row.id} selected={selectedAds.has(row.id)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedAds.has(row.id)}
                                onChange={() => handleSelectAd(row.id)}
                              />
                            </TableCell>
                            <TableCell>{row.advertiseNumber}</TableCell>
                            <TableCell>{row.title}</TableCell>
                            <TableCell>
                              {row.createdByUser?.name || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Label
                                color={selectAdvertiseStatusColor(row.status)}
                                variant="filled"
                              >
                                {row.status}
                              </Label>
                            </TableCell>
                            <TableCell>{row.price}</TableCell>
                            <TableCell>
                              {DateFormat(row.startDate)}
                            </TableCell>
                            <TableCell>
                              {DateFormat(row.endDate)}
                            </TableCell>
                            <TableCell align="right" sx={{ width: '200px', minWidth: '200px', maxWidth: '200px', padding: '8px 4px' }}>
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                              <Tooltip title="View">
                                <IconButton
                                  onClick={() => goToDetail(row.id)}
                                  color="primary"
                                  size="small"
                                >
                                  <RemoveRedEyeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  component={RouterLink}
                                  to={`/company-advertise/edit/${encryptString(row.id)}`}
                                  color="primary"
                                  size="small"
                                >
                                  <Iconify icon={'eva:edit-fill'} width={18} />
                                </IconButton>
                              </Tooltip>
                                {canPublish(row.status) && (
                                  <Tooltip title={row.status === AdvertiseStatus.expired || row.status === AdvertiseStatus.suspended ? "Republish Ad" : "Publish Ad"}>
                                    <Button
                                      variant="contained"
                                      color="success"
                                      size="small"
                                      startIcon={<Iconify icon={'eva:checkmark-circle-2-fill'} width={14} />}
                                      onClick={() => handlePublish(row.id)}
                                      disabled={publishingIds.has(row.id)}
                                      sx={{ 
                                        minWidth: 'auto', 
                                        px: 0.75, 
                                        py: 0.25, 
                                        fontSize: '0.7rem',
                                        whiteSpace: 'nowrap',
                                        textTransform: 'none'
                                      }}
                                    >
                                      {publishingIds.has(row.id) ? '...' : (row.status === AdvertiseStatus.expired || row.status === AdvertiseStatus.suspended ? 'Repub' : 'Pub')}
                                    </Button>
                                  </Tooltip>
                                )}
                                {canUnpublish(row.status) && (
                                  <Tooltip title="Unpublish Ad">
                                    <Button
                                      variant="contained"
                                      color="warning"
                                      size="small"
                                      startIcon={<Iconify icon={'eva:close-circle-2-fill'} width={14} />}
                                      onClick={() => handleUnpublish(row.id)}
                                      disabled={unpublishingIds.has(row.id)}
                                      sx={{ 
                                        minWidth: 'auto', 
                                        px: 0.75, 
                                        py: 0.25, 
                                        fontSize: '0.7rem',
                                        whiteSpace: 'nowrap',
                                        textTransform: 'none'
                                      }}
                                    >
                                      {unpublishingIds.has(row.id) ? '...' : 'Unpub'}
                                    </Button>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
              </TableContainer>

              <Box sx={{ position: 'relative', display: 'flex' }}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={limit}
                  page={currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default CompanyAdvertise;

